<script>
import { ref, onMounted, computed, defineComponent } from 'vue'
import { Toast } from 'vant'
import usePlateKeyboard from '@/components/usePlateKeyboard'
import {
  bindNoPlate,
  getPlateNoByLoginCode,
  noPlateIn,
  noPlateOut
} from '@/api/modules/parking'
import { getLoginCode } from '@/utils/authorize'
import { useRoute, useRouter } from '@/utils/useVueApi'
import {
  createPlateBindingState,
  shouldBindPlateAfterPass,
  shouldShowLoginCodeDebug
} from '../no-plate-flow'

/**
 * 历史车牌号码
 */
const HISTORY_PLATE_NUMBER = 'historyPlateNumber'

/**
 * scroll widget width
 */
const SCROLL_WIDGET_WIDTH = 40

const LOGIN_CODE_DEBUG_ENABLED =
  process.env.VUE_APP_SHOW_LOGIN_CODE === 'true'

export default defineComponent({
  name: 'ParkingEnter',
  components: {
    SuccessModal: () => import('./SuccessModal.vue')
  },
  setup() {
    const plateKeyboard = usePlateKeyboard()
    const {
      NORMAL,
      NEW_ENERGY,
      plateNumber,
      plateNumberInputMode,
      plateNumberLimitLength,
      isInputComplete,
      clearPlateNumber,
      hideKeyboard,
      setPlateNumberInputMode,
      showKeyboard
    } = plateKeyboard

    const route = useRoute()

    const router = useRouter()

    const loginCode = ref('')

    const isPlateBound = ref(false)

    const bindingReady = ref(false)

    const submitting = ref(false)

    const isExit = computed(() => 'out' in route.query)

    const showLoginCodeDebug = computed(() => {
      return shouldShowLoginCodeDebug({
        enabled: LOGIN_CODE_DEBUG_ENABLED,
        loginCode: loginCode.value
      })
    })

    const canOpenGate = computed(() => {
      return (
        isInputComplete.value &&
        bindingReady.value &&
        Boolean(loginCode.value) &&
        !submitting.value
      )
    })

    /**
     * 新能源车牌
     * 并且是最后一位车牌号码
     * 并且当前输入车牌号码长度不等于车牌号码长度
     * @param {number} index
     */
    const isNewEnergyClassName = (index) => {
      if (
        plateNumberInputMode.value === NEW_ENERGY &&
        index === plateNumberLimitLength.value &&
        plateNumber.value.length !== plateNumberLimitLength.value
      ) {
        return true
      }

      return false
    }

    /**
     * 设置历史车牌号码
     */
    const setHistoryPlateNumber = () => {
      localStorage.setItem(HISTORY_PLATE_NUMBER, plateNumber.value)
      historyPlateNumber.value = getHistoryPlateNumber()
    }

    /**
     * 获取历史车牌号码
     */
    const getHistoryPlateNumber = () => {
      return localStorage.getItem(HISTORY_PLATE_NUMBER)
    }

    /**
     * 历史车牌号码
     */
    const historyPlateNumber = ref(getHistoryPlateNumber())

    /**
     * 打开道闸
     */
    const openGate = async () => {
      if (!canOpenGate.value) {
        return
      }

      const handle = isExit.value ? noPlateOut : noPlateIn

      try {
        submitting.value = true
        const res = await handle({
          plateNo: plateNumber.value,
          parkId: route.query.parkId,
          laneCode: route.query.laneCode,
          loginCode: loginCode.value
        })

        if (
          shouldBindPlateAfterPass({
            isExit: isExit.value,
            isBound: isPlateBound.value
          })
        ) {
          await bindNoPlate({
            loginCode: loginCode.value,
            plateNo: plateNumber.value
          })
          isPlateBound.value = true
          hideKeyboard()
        }

        setHistoryPlateNumber()

        if (!res) {
          showModal.value = true
          return
        }

        router.push({
          name: 'ParkingFee',
          query: {
            ...route.query,
            noPlate: '1',
            plateNo: plateNumber.value
          }
        })
      } catch (error) {
        console.log(error)
      } finally {
        submitting.value = false
      }
    }

    /**
     * 快速填入车牌号码
     */
    const quickInputPlateNumber = () => {
      if (historyPlateNumber.value.length > plateNumberLimitLength.value) {
        // 去除最后一位
        plateNumber.value = historyPlateNumber.value.slice(
          0,
          plateNumberLimitLength.value
        )
      } else {
        plateNumber.value = historyPlateNumber.value
      }
    }

    const tabBarDom = ref(null)

    const selectPlateNumberInputMode = (mode) => {
      if (!isPlateBound.value) {
        setPlateNumberInputMode(mode)
      }
    }

    const openPlateKeyboard = () => {
      if (!isPlateBound.value && bindingReady.value) {
        showKeyboard()
      }
    }

    const copyLoginCode = async () => {
      try {
        let copied = false
        if (navigator.clipboard && window.isSecureContext) {
          try {
            await navigator.clipboard.writeText(loginCode.value)
            copied = true
          } catch (error) {
            copied = false
          }
        }

        if (!copied) {
          const textarea = document.createElement('textarea')
          textarea.value = loginCode.value
          textarea.setAttribute('readonly', '')
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          try {
            textarea.select()
            textarea.setSelectionRange(0, textarea.value.length)
            if (!document.execCommand('copy')) {
              throw new Error('copy failed')
            }
          } finally {
            document.body.removeChild(textarea)
          }
        }
        Toast('已复制')
      } catch (error) {
        Toast('复制失败，请长按复制')
      }
    }

    onMounted(async () => {
      tabBarDom.value = document.querySelector('.tab-bar')
      clearPlateNumber()
      hideKeyboard()
      loginCode.value = getLoginCode(route.query.parkId) || ''

      if (!loginCode.value) {
        return
      }

      try {
        const boundPlateNo = await getPlateNoByLoginCode({
          loginCode: loginCode.value
        })
        const bindingState = createPlateBindingState(boundPlateNo)
        const inputMode =
          bindingState.plateNo.length === 8 ? NEW_ENERGY : NORMAL

        setPlateNumberInputMode(inputMode)
        plateNumber.value = bindingState.plateNo
        isPlateBound.value = bindingState.isBound
        bindingReady.value = true

        if (bindingState.isBound) {
          hideKeyboard()
        }
      } catch (error) {
        console.log(error)
      }
    })

    /**
     * 滑动的位置
     */
    const scrollLeft = computed(() => {
      const index = plateNumberInputMode.value === NEW_ENERGY ? 1 : 0
      const tabBarW = tabBarDom.value?.offsetWidth
      const itemWidth = tabBarW / 2

      return index * itemWidth + itemWidth / 2 - SCROLL_WIDGET_WIDTH / 2
    })

    /**
     * 显示modal
     */
    const showModal = ref(false)

    return {
      ...plateKeyboard,
      historyPlateNumber,
      setHistoryPlateNumber,
      getHistoryPlateNumber,
      isNewEnergyClassName,
      openGate,
      quickInputPlateNumber,
      SCROLL_WIDGET_WIDTH,
      scrollLeft,
      showModal,
      isPlateBound,
      canOpenGate,
      loginCode,
      showLoginCodeDebug,
      copyLoginCode,
      selectPlateNumberInputMode,
      openPlateKeyboard
    }
  }
})
</script>

<template>
  <div class="content-container">
    <div class="tab-bar">
      <div
        :class="[
          'item',
          plateNumberInputMode === NORMAL ? 'active' : '',
          'normal'
        ]"
        @click.stop="selectPlateNumberInputMode(NORMAL)"
      >
        燃油车
      </div>
      <div
        :class="[
          'item',
          plateNumberInputMode === NEW_ENERGY ? 'active' : '',
          'new-energy'
        ]"
        @click.stop="selectPlateNumberInputMode(NEW_ENERGY)"
      >
        新能源
      </div>
      <div
        class="scroll-bar"
        :style="{
          transform: `translateX(${scrollLeft}px)`,
          width: `${SCROLL_WIDGET_WIDTH}px`
        }"
      />
    </div>
    <div class="content">
      <div
        class="plate-number"
        :class="[historyPlateNumber ? '' : 'ignore-history']"
      >
        <div class="text">车牌号码</div>
        <div class="input-box" @click.stop="openPlateKeyboard">
          <div
            v-for="(p, i) in plateNumberLimitLength"
            :key="i"
            :class="[
              'number',
              isNewEnergyClassName(p) ? 'new-energy' : '',
              plateNumber[i] ? 'active' : ''
            ]"
          >
            {{ plateNumber[i] }}
          </div>
        </div>
      </div>

      <div v-if="historyPlateNumber && !isPlateBound" class="history">
        <div class="old-plate">
          历史车牌:
          <span @click.stop="quickInputPlateNumber">{{
            historyPlateNumber
          }}</span>
        </div>
        <div class="quick-input" @click.stop="quickInputPlateNumber">
          <i class="icon-top-arrow" />
          <span>快速填入</span>
        </div>
      </div>

      <div
        :class="['submit', canOpenGate ? '' : 'disabled']"
        @click="openGate"
      >
        打开道闸
      </div>

      <div v-if="showLoginCodeDebug" class="login-code-debug">
        <div class="debug-header">
          <span>loginCode</span>
          <button
            type="button"
            class="copy-button"
            aria-label="复制 loginCode"
            title="复制 loginCode"
            @click.stop="copyLoginCode"
          >
            <van-icon name="records" size="18" />
          </button>
        </div>
        <div class="debug-value">{{ loginCode }}</div>
      </div>
    </div>

    <SuccessModal v-model="showModal" />
  </div>
</template>

<style lang="scss" scoped>
$themeColor: #3476fe;

// 伪元素 border
@mixin pseudo-border {
  content: '';
  position: absolute;
  left: -50px;
  width: 150%;
  height: 1px;
  background-color: #f5f5f5;
}

.content-container {
  padding-bottom: 19px;

  .tab-bar {
    display: flex;
    align-items: center;
    height: 51px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 50%;
      z-index: 2;
      background-color: #f7f7f7;
    }

    .scroll-bar {
      height: 3px;
      background-color: $themeColor;
      position: absolute;
      z-index: 2;
      left: 0;
      bottom: 0;
      transition: all 0.3s;
    }

    .item {
      flex: 1;
      text-align: center;
      font-size: 16px;
      font-weight: 400;
      color: #999;
      height: 51px;
      line-height: 51px;
      position: relative;
      z-index: 2;
      background-color: #f7f7f7;

      &.normal {
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
      }

      &.new-energy {
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
      }

      &.active {
        color: $themeColor;
        background-color: #fff;
      }
    }
  }
  .content {
    padding: 0 16px;
    margin-top: 16px;

    .plate-number {
      margin-bottom: 22px;

      &.ignore-history {
        position: relative;
        padding-bottom: 22px;
        margin-bottom: 16px;

        &::before {
          @include pseudo-border;
          bottom: 0;
        }
      }

      .text {
        font-size: 16px;
        font-weight: 400;
        color: #333;
        display: flex;

        &::before {
          content: '*';
          color: #ec1818;
          transform: scale(1.4) translate(-1px, 2px);
        }
      }

      .input-box {
        height: 48px;
        display: flex;
        justify-content: space-between;
        margin-top: 16px;

        .number {
          width: 34px;
          height: 100%;
          border: 1px solid #999;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          color: #333;

          &.active {
            border-color: $themeColor;
            color: $themeColor;
          }

          &.new-energy {
            background-color: #edfff0;
            border: 1px dashed #88ca91;
            position: relative;

            &::before {
              content: '+';
              position: absolute;
              top: 6px;
              left: 50%;
              transform: translateX(-50%) scale(1.8);
              color: #88ca91;
            }

            &::after {
              content: '新能源';
              position: absolute;
              left: 50%;
              bottom: 8px;
              transform: translateX(-50%) scale(0.9);
              font-size: 10px;
              color: #88ca91;
              width: 100%;
              font-weight: bold;
              text-align: center;
            }
          }
        }
      }
    }

    .history {
      display: flex;
      align-items: center;
      font-size: 14px;
      font-weight: 400;
      padding: 15px 0;
      position: relative;

      &::before,
      &::after {
        @include pseudo-border;
      }

      &::before {
        top: 0;
      }

      &::after {
        bottom: 0;
        opacity: 0.3;
      }

      .old-plate {
        flex: 1;
        color: #666;

        span {
          color: #999;
          margin-left: 5px;
        }
      }

      .quick-input {
        color: $themeColor;
        display: flex;
        align-items: center;

        .icon-top-arrow {
          display: block;
          width: 14px;
          height: 14px;
          background: url('~@/assets/images/parking-enter/icon-top-arrow.png')
            no-repeat center center / 100% 100%;
        }

        span {
          margin-left: 3px;
        }
      }
    }

    .submit {
      margin-top: 16px;
      height: 50px;
      line-height: 50px;
      text-align: center;
      font-size: 18px;
      font-weight: 400;
      color: #fff;
      background-color: $themeColor;
      border-radius: 4px;
      position: relative;

      &.disabled {
        opacity: 0.64;
      }
    }

    .login-code-debug {
      margin-top: 16px;
      padding-top: 12px;
      border-top: 1px dashed #d9d9d9;

      .debug-header {
        min-height: 32px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: #666;
        font-size: 13px;
      }

      .copy-button {
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        background: transparent;
        color: $themeColor;
      }

      .debug-value {
        margin-top: 6px;
        padding: 10px;
        background-color: #f7f8fa;
        color: #333;
        font-size: 12px;
        line-height: 18px;
        word-break: break-all;
        user-select: all;
      }
    }
  }
}
</style>
